import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Tooltip,
  Modal,
  Button,
  Slider,
  TextField,
  IconButton
} from '@mui/material';
import IconWithProgress from './IconWithProgress';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { generateResidualRisk, updateResidualRisk, analyzeActionPlan, saveResidualRisk } from './redux/actions/residualRiskActions';

// Priority color mapping (for the Priority chip in the table)
const riskPriorityColors = {
  Critical: '#1A1A1A',
  High: '#993333',
  Significant: '#B94D4D',
  Moderate: '#D4AA7D',
  Low: '#B8C2B7',
  Negligible: '#D8D8D8',
  None: '#EBEBEB',
};

// --------------------------------------------------
// 1) Residual Risk arrays
//    We'll store levels & parallel color codes

const RESIDUAL_RISK_LEVELS = [
  'Negligible',
  'Low',
  'Moderate',
  'Significant',
  'High',
  'Critical',
];
const RESIDUAL_RISK_COLORS = [
  '#D8D8D8', // Negligible
  '#B8C2B7', // Low
  '#D4AA7D', // Moderate
  '#B94D4D', // Significant
  '#993333', // High
  '#1A1A1A', // Critical
];

// A helper to convert residual risk text → numeric index
function residualRiskToIndex(riskText = '') {
  const idx = RESIDUAL_RISK_LEVELS.findIndex(
    (level) => level.toLowerCase() === riskText.toLowerCase()
  );
  return idx >= 0 ? idx : 1; // default to 'Low'
}

// A helper to get text from numeric index
function indexToResidualRisk(idx) {
  return RESIDUAL_RISK_LEVELS[idx] || 'Low';
}

// A helper to get color from numeric index
function getResidualRiskColor(idx) {
  return RESIDUAL_RISK_COLORS[idx] || '#EBEBEB';
}

// We'll define slider marks from the array, so it automatically shows labels
const residualRiskMarks = RESIDUAL_RISK_LEVELS.map((label, i) => ({
  value: i,
  label,
}));

// --------------------------------------------------
// 2) Control Efficiency arrays
//    "Excellent", "Very Good", "Good", "Fair", "Poor", "Unsatisfactory"

const ACTION_PLAN_LEVELS = [
  'Unsatisfactory',
  'Poor',
  'Fair',
  'Good',
  'Very Good',
  'Excellent',
];

const ACTION_PLAN_COLORS = [
  '#D32F2F', // Unsatisfactory
  '#FF5722', // Poor
  '#FFC107', // Fair
  '#8BC34A', // Good
  '#66BB6A', // Very Good
  '#4CAF50', // Excellent
];

function actionPlanToIndex(ratingText = '') {
  const idx = ACTION_PLAN_LEVELS.findIndex(
    (level) => level.toLowerCase() === ratingText.toLowerCase()
  );
  return idx >= 0 ? idx : 0; // default to 'Unsatisfactory'
}

function indexToActionPlan(idx) {
  return ACTION_PLAN_LEVELS[idx] || 'Unsatisfactory';
}

function getActionPlanColor(idx) {
  return ACTION_PLAN_COLORS[idx] || '#EBEBEB';
}

const actionPlanMarks = ACTION_PLAN_LEVELS.map((label, i) => ({
  value: i,
  label,
}));

function canGenerateRisk(risk, controlsArr, savedControlEvaluations) {
  const missing = [];
  if (!risk.priority) {
    missing.push('Missing Risk Evaluation');
  }
  if (!controlsArr || controlsArr.length === 0) {
    missing.push('No Controls Saved');
  } else {
    const allEvaluated = controlsArr.every((ctrl) => {
      const evals = (savedControlEvaluations[risk.riskId] || {})[ctrl.controlId];
      return evals && evals.length > 0;
    });
    if (!allEvaluated) {
      missing.push('Some Controls Missing Evaluations');
    }
  }
  return { canGenerate: missing.length === 0, missingReason: missing.join(' & ') };
}

function ResidualRisk({ modelId, assessmentUuid, sectionName, riskId = null, hideTitle = false }) {
  const singleRiskMode = !!riskId;
  const dispatch = useDispatch();
  const residualRiskState = useSelector((state) => state.residualRisk.byModel[modelId] || {});
  // Pull every section's risks
const riskFormData = useSelector(
  (s) => s.risks.byAssessment[modelId]?.modelRisks || {}
);

const allSavedRisks = useMemo(() => {
  // riskFormData[section] → { areaRisks: { category: [risks] } | [risks] }
  return Object.entries(riskFormData).flatMap(
    ([section, { areaRisks = {} }]) => {
      // Some older sections may store an array directly; handle both shapes
      if (Array.isArray(areaRisks)) {
        return areaRisks
          .filter((r) => r?.status === 'Saved')
          .map((r) => ({ ...r, section, category: r.category || 'Uncategorized' }));
      }
      // Standard shape: object keyed by category
      return Object.entries(areaRisks).flatMap(
        ([category, risks = []]) =>
          risks
            .filter((r) => r?.status === 'Saved')
            .map((r) => ({ ...r, section, category }))
      );
    }
  );
}, [riskFormData]);
  const savedControls = useSelector((state) => state.controls.savedControls || {});
  const savedControlEvaluations = useSelector(
    (state) => state.controls.savedControlEvaluations || {}
  );

  // const groupedRisks = useMemo(() => {
  //   const allSaved = Object.entries(sectionRisks).flatMap(([categoryName, risks]) =>
  //     risks.filter(r => r?.status === 'Saved').map(r => ({ ...r, category: categoryName }))
  //   );
  //   const filtered = riskId
  //     ? allSaved.filter(r => r.riskId === riskId)
  //     : allSaved;
  //   return filtered.reduce((acc, risk) => {
  //     const cat = risk.category || 'Uncategorized';
  //     if (!acc[cat]) acc[cat] = [];
  //     acc[cat].push(risk);
  //     return acc;
  //   }, {});
  // }, [sectionRisks, riskId]);
  const groupedRisks = useMemo(() => {
    let list = allSavedRisks;
    if (riskId) list = list.filter((r) => r.riskId === riskId);
    else if (sectionName) list = list.filter((r) => r.section === sectionName);

    return list.reduce((acc, r) => {
      const sec = r.section || 'Unassigned';
      if (!acc[sec]) acc[sec] = [];
      acc[sec].push(r);
      return acc;
    }, {});
  }, [allSavedRisks, riskId, sectionName]);

  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState({
    riskId: null,
    riskName: '',
    priority: '',
    residualRating: '',
    residualRationale: '',
    actionPlanRating: '',
    actionPlanRationale: '',
    actionPlanShortSummary: '',
  });

  const buildSavePayload = (risk) => ({
    modelId,
    assessmentUuid,
    riskId: risk.riskId,
    riskName: risk.riskName,
    priority: risk.priority,
    residual_rating: risk.residual_rating || 'N/A',
    residual_explanation: risk.residual_explanation || 'No rationale',
    action_plan_rating: risk.action_plan_rating || 'N/A',
    action_plan_analysis: risk.action_plan_analysis || 'No analysis',
    action_plan_short_summary: risk.action_plan_short_summary || '',
    evaluator_id: 'user@example.com',
    comments: '',
  });

  const buildOverridePayload = () => ({
    modelId,
    assessmentUuid,
    riskId: overrideTarget.riskId,
    riskName: overrideTarget.riskName,
    priority: overrideTarget.priority,
    residual_rating: overrideTarget.residualRating || 'N/A',
    residual_explanation: overrideTarget.residualRationale || 'No rationale',
    action_plan_rating: overrideTarget.actionPlanRating || 'N/A',
    action_plan_analysis: overrideTarget.actionPlanRationale || 'No rationale',
    action_plan_short_summary: overrideTarget.actionPlanShortSummary || '',
    evaluator_id: 'user@example.com',
    comments: '',
  });

  const handleGenerateAll = async (risk) => {
    await dispatch(analyzeActionPlan(modelId, risk.riskId, risk.riskName));
    await dispatch(generateResidualRisk(modelId, risk.riskId));
  };

  const handleOpenOverrideModal = (risk) => {
    const rr = residualRiskState[risk.riskId] || {};
    const finalRating = rr.overridden?.residual_rating ?? rr.residual_rating ?? 'N/A';
    const finalRationale = rr.overridden?.residual_explanation ?? rr.residual_explanation ?? 'No rationale';
    const finalAPRating = rr.overridden?.action_plan_rating ?? rr.action_plan_rating ?? 'N/A';
    const finalAPRationale = rr.overridden?.action_plan_analysis ?? rr.action_plan_analysis ?? 'No rationale';
    setOverrideTarget({
      riskId: risk.riskId,
      riskName: risk.riskName,
      priority: risk.priority,
      residualRating: finalRating,
      residualRationale: finalRationale,
      actionPlanRating: finalAPRating,
      actionPlanRationale: finalAPRationale,
      actionPlanShortSummary: rr.action_plan_short_summary || '',
    });
    setOverrideModalOpen(true);
  };

  const handleCloseOverrideModal = () => setOverrideModalOpen(false);
  const handleSaveOverride = () => {
    dispatch(
      updateResidualRisk(modelId, overrideTarget.riskId, {
        residual_rating: overrideTarget.residualRating,
        residual_explanation: overrideTarget.residualRationale,
        action_plan_rating: overrideTarget.actionPlanRating,
      })
    );
    dispatch(saveResidualRisk(buildOverridePayload()));
    setOverrideModalOpen(false);
  };

  return (
    <Box sx={{ backgroundColor: '#F9FAFB', p: 3, minHeight: '100vh' }}>
      {!hideTitle && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            pb: 2,
            
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: '#374151', // Slightly lighter than main titles
              fontSize: '1rem', // 16px - more appropriate for section headers
              letterSpacing: '-0.01em',
              lineHeight: 1.4,
              ml:1
            }}
          >
            Residual Risk & Action Plan Analysis
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: '#F9FAFB',
          borderRadius: 2,
          border: '1px solid #E5E7EB',
          mb: 3,
        }}
      >
        <Box
          sx={{ backgroundColor: '#FFFFFF', borderRadius: 2, overflow: 'hidden' }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ backgroundColor: '#F9FAFB', color: '#6B7280', fontWeight: 500, fontSize: '0.875rem' }}
                  >
                    Risk Name
                  </TableCell>
                  {singleRiskMode && (
                    <TableCell
                      sx={{ backgroundColor: '#F9FAFB', color: '#6B7280', fontWeight: 500, fontSize: '0.875rem' }}
                    >
                      Risk Source
                    </TableCell>
                  )}
                  <TableCell
                    sx={{ backgroundColor: '#F9FAFB', color: '#6B7280', fontWeight: 500, fontSize: '0.875rem' }}
                  >
                    Priority
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#F9FAFB', color: '#6B7280', fontWeight: 500, fontSize: '0.875rem' }}
                  >
                    Action Plan
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#F9FAFB', color: '#6B7280', fontWeight: 500, fontSize: '0.875rem' }}
                  >
                    Residual Risk
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#F9FAFB', color: '#6B7280', fontWeight: 500, fontSize: '0.875rem' }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {singleRiskMode
                  ? Object.values(groupedRisks)
                      .flat()
                      .map((risk) => {
                        const { canGenerate, missingReason } = canGenerateRisk(
                          risk,
                          savedControls[risk.riskId] || [],
                          savedControlEvaluations
                        );
                        const rr = residualRiskState[risk.riskId] || {};
                        const finalAPRating =
                          rr.overridden?.action_plan_rating ?? rr.action_plan_rating ?? 'N/A';
                        const apRationale = rr.action_plan_analysis || 'No rationale';
                        const finalResidualRating =
                          rr.overridden?.residual_rating ?? rr.residual_rating ?? 'N/A';
                        const residualRationale =
                          rr.overridden?.residual_explanation ?? rr.residual_explanation ?? 'No rationale';
                        const priorityText =
                          risk.priority?.charAt(0).toUpperCase() + risk.priority?.slice(1).toLowerCase() || 'None';
                        const priorityColor = riskPriorityColors[priorityText] || riskPriorityColors.None;
                        const apIndex = actionPlanToIndex(finalAPRating);
                        const rrIndex = residualRiskToIndex(finalResidualRating);

                        return (
                          <React.Fragment key={risk.riskId}>
                            <TableRow>
                              <TableCell
                                colSpan={6}
                                sx={{
                                  backgroundColor: '#F0F0F0',
                                  color: '#111827',
                                  fontWeight: 600,
                                }}
                              >
                                {risk.category}
                              </TableCell>
                            </TableRow>
                            <TableRow
                              sx={{ '&:last-child td': { border: 0 }, '& td': { borderBottom: '1px solid #F3F4F6' } }}
                            >
                              <TableCell>{risk.riskName}</TableCell>
                              <TableCell>{risk.category}</TableCell>
                              <TableCell>
                                <Chip label={priorityText} sx={{ backgroundColor: priorityColor, color: '#FFFFFF' }} />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={finalAPRating}
                                    onClick={() => handleOpenOverrideModal(risk)}
                                    sx={{ backgroundColor: getActionPlanColor(apIndex), color: '#FFFFFF', cursor: 'pointer' }}
                                  />
                                  <Tooltip
                                    title={
                                      typeof apRationale === 'object' ? (
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content auto', gap: 1, p: 1 }}>
                                          {Object.entries(apRationale).map(([key, value]) => (
                                            <React.Fragment key={key}>
                                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{key}:</Typography>
                                              <Typography variant="caption">{value}</Typography>
                                            </React.Fragment>
                                          ))}
                                        </Box>
                                      ) : (
                                        apRationale
                                      )
                                    }
                                    componentsProps={{ tooltip: { sx: { maxWidth: 500, fontSize: '0.875rem', whiteSpace: 'normal' } } }}
                                  >
                                    <IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={finalResidualRating}
                                    onClick={() => handleOpenOverrideModal(risk)}
                                    sx={{ backgroundColor: getResidualRiskColor(rrIndex), color: '#FFFFFF', cursor: 'pointer' }}
                                  />
                                  <Tooltip title={residualRationale}><IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton></Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {!canGenerate && (
                                    <Tooltip title={missingReason}><span><IconWithProgress isLoading={false} onClick={() => {}} tooltip="Cannot generate" disabled /></span></Tooltip>
                                  )}
                                  {canGenerate && (
                                    <IconWithProgress isLoading={rr.loading || rr.actionPlanLoading} onClick={() => handleGenerateAll(risk)} tooltip="Analyze Action Plan + Generate Residual" />
                                  )}
                                  <Tooltip title="Save Evaluation"><IconButton size="medium" onClick={() => dispatch(saveResidualRisk(buildSavePayload(risk)))}><SaveIcon fontSize="inherit" /></IconButton></Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })
                  : Object.entries(groupedRisks).map(([section, risks]) => (
                      <React.Fragment key={section}>
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            sx={{
                              backgroundColor: '#F0F0F0',
                              color: '#111827',
                              fontWeight: 600,
                            }}
                          >
                            {section}
                          </TableCell>
                        </TableRow>
                        {risks.map((risk) => {
                          const { canGenerate, missingReason } = canGenerateRisk(
                            risk,
                            savedControls[risk.riskId] || [],
                            savedControlEvaluations
                          );
                          const rr = residualRiskState[risk.riskId] || {};
                          const finalAPRating = rr.overridden?.action_plan_rating ?? rr.action_plan_rating ?? 'N/A';
                          const apRationale = rr.action_plan_analysis || 'No rationale';
                          const finalResidualRating = rr.overridden?.residual_rating ?? rr.residual_rating ?? 'N/A';
                          const residualRationale = rr.overridden?.residual_explanation ?? rr.residual_explanation ?? 'No rationale';
                          const priorityText = risk.priority?.charAt(0).toUpperCase() + risk.priority?.slice(1).toLowerCase() || 'None';
                          const priorityColor = riskPriorityColors[priorityText] || riskPriorityColors.None;
                          const apIndex = actionPlanToIndex(finalAPRating);
                          const rrIndex = residualRiskToIndex(finalResidualRating);

                          return (
                            <TableRow key={risk.riskId} sx={{ '&:last-child td': { border: 0 }, '& td': { borderBottom: '1px solid #F3F4F6' } }}>
                              <TableCell>{risk.riskName}</TableCell>
                              <TableCell><Chip label={priorityText} sx={{ backgroundColor: priorityColor, color: '#FFFFFF' }} /></TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip label={finalAPRating} onClick={() => handleOpenOverrideModal(risk)} sx={{ backgroundColor: getActionPlanColor(apIndex), color: '#FFFFFF', cursor: 'pointer' }} />
                                  <Tooltip title={apRationale}><IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton></Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip label={finalResidualRating} onClick={() => handleOpenOverrideModal(risk)} sx={{ backgroundColor: getResidualRiskColor(rrIndex), color: '#FFFFFF', cursor: 'pointer' }} />
                                  <Tooltip title={residualRationale}><IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton></Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {!canGenerate && (
                                    <Tooltip title={missingReason}><span><IconWithProgress isLoading={false} onClick={() => {}} tooltip="Cannot generate" disabled /></span></Tooltip>
                                  )}
                                  {canGenerate && (
                                    <IconWithProgress isLoading={rr.loading || rr.actionPlanLoading} onClick={() => handleGenerateAll(risk)} tooltip="Analyze Action Plan + Generate Residual" />
                                  )}
                                  <Tooltip title="Save Evaluation"><IconButton size="medium" onClick={() => dispatch(saveResidualRisk(buildSavePayload(risk)))}><SaveIcon fontSize="inherit" /></IconButton></Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {overrideModalOpen && (
        <Modal
          open={overrideModalOpen}
          onClose={() => setOverrideModalOpen(false)}
          disableEnforceFocus
        >
          {/* Override modal content here */}
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: '#FFFFFF', p: 3, borderRadius: 2, width: 400 }}>
            <Typography variant="h6" gutterBottom>Override Residual & Action Plan</Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Residual Rating</Typography>
              <Slider
                value={residualRiskToIndex(overrideTarget.residualRating)}
                onChange={(_, val) => setOverrideTarget(prev => ({ ...prev, residualRating: indexToResidualRisk(val) }))}
                step={1}
                marks={residualRiskMarks}
                min={0}
                max={residualRiskMarks.length - 1}
                valueLabelDisplay="auto"
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Residual Explanation"
                multiline
                rows={3}
                fullWidth
                value={overrideTarget.residualRationale}
                onChange={(e) => setOverrideTarget(prev => ({ ...prev, residualRationale: e.target.value }))}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Action Plan Rating</Typography>
              <Slider
                value={actionPlanToIndex(overrideTarget.actionPlanRating)}
                onChange={(_, val) => setOverrideTarget(prev => ({ ...prev, actionPlanRating: indexToActionPlan(val) }))}
                step={1}
                marks={actionPlanMarks}
                min={0}
                max={actionPlanMarks.length - 1}
                valueLabelDisplay="auto"
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Action Plan Rationale"
                multiline
                rows={3}
                fullWidth
                value={overrideTarget.actionPlanRationale}
                onChange={(e) => setOverrideTarget(prev => ({ ...prev, actionPlanRationale: e.target.value }))}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Action Plan Short Summary"
                multiline
                rows={2}
                fullWidth
                value={overrideTarget.actionPlanShortSummary}
                onChange={(e) => setOverrideTarget(prev => ({ ...prev, actionPlanShortSummary: e.target.value }))}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={handleCloseOverrideModal} variant="outlined">Cancel</Button>
              <Button onClick={handleSaveOverride} variant="contained" color="primary" startIcon={<SaveIcon />}>Save Override</Button>
            </Box>
          </Box>
        </Modal>
      )}
    </Box>
  );
}

export default ResidualRisk;